import clsx from 'clsx';
import type { ChangeEvent, FocusEvent, ForwardedRef } from 'react';
import { __, isRTL } from '@wordpress/i18n';
import { useRef, useState, forwardRef } from '@wordpress/element';
import { useInstanceId, useMergeRefs } from '@wordpress/compose';
import BaseControl from '../base-control';
import Button from '../button';
import Icon from '../icon';
import {
	floatClamp,
	useControlledRangeValue,
	getCSSWideKeyword,
} from './utils';
import { clamp } from '../utils/math';
import InputRange from './input-range';
import RangeRail from './rail';
import SimpleTooltip from './tooltip';
import NumberControl from '../number-control';
import styles from './style.module.scss';
import type { RangeControlProps } from './types';
import type { WordPressComponentProps } from '../context';

const NUMBER_CONTROL_WIDTH = 80;

const noop = () => {};

/**
 * Computes the value that `RangeControl` should reset to when pressing
 * the reset button.
 */
function computeResetValue( {
	resetFallbackValue,
	initialPosition,
}: Pick< RangeControlProps, 'resetFallbackValue' | 'initialPosition' > ) {
	if ( resetFallbackValue !== undefined ) {
		return ! Number.isNaN( resetFallbackValue ) ? resetFallbackValue : null;
	}

	if ( initialPosition !== undefined ) {
		return ! Number.isNaN( initialPosition ) ? initialPosition : null;
	}

	return null;
}

function UnforwardedRangeControl(
	props: WordPressComponentProps< RangeControlProps, 'input', false >,
	forwardedRef: ForwardedRef< HTMLInputElement >
) {
	const {
		// Prevent passing legacy props to internal component.
		__nextHasNoMarginBottom: _,
		__next40pxDefaultSize: _next40pxDefaultSize,
		afterIcon,
		allowReset = false,
		beforeIcon,
		className,
		color: colorProp,
		currentInput,
		disabled = false,
		help,
		hideLabelFromVision = false,
		initialPosition,
		isShiftStepEnabled = true,
		label,
		marks = false,
		max = 100,
		min = 0,
		onBlur = noop,
		onChange = noop,
		onFocus = noop,
		onMouseLeave = noop,
		onMouseMove = noop,
		railColor,
		renderTooltipContent = ( v ) => v,
		resetFallbackValue,
		shiftStep = 10,
		showTooltip: showTooltipProp,
		size = 'default',
		step = 1,
		trackColor,
		value: valueProp,
		withInputField = true,
		...otherProps
	} = props;

	const [ value, setValue ] = useControlledRangeValue( {
		min,
		max,
		value: valueProp ?? null,
		initial: initialPosition,
	} );
	const isResetPendent = useRef( false );

	let hasTooltip = showTooltipProp;
	let hasInputField = withInputField;

	if ( step === 'any' ) {
		// The tooltip and number input field are hidden when the step is "any"
		// because the decimals get too lengthy to fit well.
		hasTooltip = false;
		hasInputField = false;
	}

	const [ showTooltip, setShowTooltip ] = useState( hasTooltip );
	const [ isFocused, setIsFocused ] = useState( false );

	const inputRef = useRef< HTMLInputElement >( null );
	const isCurrentlyFocused = inputRef.current?.matches( ':focus' );
	const isThumbFocused = ! disabled && isFocused;

	const isValueReset = value === null;
	const currentValue = value !== undefined ? value : currentInput;

	const inputSliderValue = isValueReset ? '' : currentValue;
	const rangeFillValue = isValueReset ? ( max - min ) / 2 + min : value;

	const fillValue = isValueReset
		? 50
		: ( ( value - min ) / ( max - min ) ) * 100;
	const fillValueOffset = `${ clamp( fillValue, 0, 100 ) }%`;

	const classes = clsx( 'components-range-control', className );

	const colorKeyword = getCSSWideKeyword( colorProp );
	const trackColorKeyword = getCSSWideKeyword( trackColor );

	const wrapperClasses = clsx(
		'components-range-control__wrapper',
		styles.wrapper,
		colorProp === undefined && styles[ 'default-color' ],
		colorKeyword && styles[ `color-${ colorKeyword }` ],
		!! marks && 'is-marked'
	);

	const id = useInstanceId(
		UnforwardedRangeControl,
		'inspector-range-control'
	);
	const describedBy = !! help ? `${ id }__help` : undefined;
	const enableTooltip = hasTooltip !== false && Number.isFinite( value );

	const handleOnRangeChange = ( event: ChangeEvent< HTMLInputElement > ) => {
		const nextValue = parseFloat( event.target.value );
		setValue( nextValue );
		onChange( nextValue );
	};

	const handleOnChange = ( next?: string ) => {
		// @ts-expect-error TODO: Investigate if it's problematic for setValue() to
		// potentially receive a NaN when next is undefined.
		let nextValue = parseFloat( next );
		setValue( nextValue );

		/*
		 * Calls onChange only when nextValue is numeric
		 * otherwise may queue a reset for the blur event.
		 */
		if ( ! isNaN( nextValue ) ) {
			if ( nextValue < min || nextValue > max ) {
				nextValue = floatClamp( nextValue, min, max ) as number;
			}

			onChange( nextValue );
			isResetPendent.current = false;
		} else if ( allowReset ) {
			isResetPendent.current = true;
		}
	};

	const handleOnInputNumberBlur = () => {
		if ( isResetPendent.current ) {
			handleOnReset();
			isResetPendent.current = false;
		}
	};

	const handleOnReset = () => {
		// Reset to `resetFallbackValue` if defined, otherwise set internal value
		// to `null` — which, if propagated to the `value` prop, will cause
		// the value to be reset to the `initialPosition` prop if defined.
		const resetValue = Number.isNaN( resetFallbackValue )
			? null
			: ( resetFallbackValue ?? null );

		setValue( resetValue );

		/**
		 * Previously, this callback would always receive undefined as
		 * an argument. This behavior is unexpected, specifically
		 * when resetFallbackValue is defined.
		 *
		 * The value of undefined is not ideal. Passing it through
		 * to internal <input /> elements would change it from a
		 * controlled component to an uncontrolled component.
		 *
		 * For now, to minimize unexpected regressions, we're going to
		 * preserve the undefined callback argument, except when a
		 * resetFallbackValue is defined.
		 */
		onChange( resetValue ?? undefined );
	};

	const handleShowTooltip = () => setShowTooltip( true );
	const handleHideTooltip = () => setShowTooltip( false );

	const handleOnBlur = ( event: FocusEvent< HTMLInputElement > ) => {
		onBlur( event );
		setIsFocused( false );
		handleHideTooltip();
	};

	const handleOnFocus = ( event: FocusEvent< HTMLInputElement > ) => {
		onFocus( event );
		setIsFocused( true );
		handleShowTooltip();
	};

	const offsetStyle = {
		[ isRTL() ? 'right' : 'left' ]: fillValueOffset,
	};

	return (
		<BaseControl
			className={ classes }
			label={ label }
			hideLabelFromVision={ hideLabelFromVision }
			id={ `${ id }` }
			help={ help }
		>
			<div
				className={ clsx(
					'components-range-control__root',
					styles.root,
					{
						[ styles[ 'is-rtl' ] ]: isRTL(),
						[ styles[ 'is-compact' ] ]: size === 'compact',
					}
				) }
			>
				{ beforeIcon && (
					<span className={ styles[ 'before-icon' ] }>
						<Icon icon={ beforeIcon } />
					</span>
				) }
				<div
					className={ wrapperClasses }
					style={
						{
							'--_wp-components-range-control-color': colorProp,
						} as React.CSSProperties
					}
				>
					<InputRange
						{ ...otherProps }
						className="components-range-control__slider"
						describedBy={ describedBy }
						disabled={ disabled }
						id={ `${ id }` }
						label={ label }
						max={ max }
						min={ min }
						onBlur={ handleOnBlur }
						onChange={ handleOnRangeChange }
						onFocus={ handleOnFocus }
						onMouseMove={ onMouseMove }
						onMouseLeave={ onMouseLeave }
						ref={ useMergeRefs( [ inputRef, forwardedRef ] ) }
						step={ step }
						value={ inputSliderValue ?? undefined }
					/>
					<RangeRail
						aria-hidden
						disabled={ disabled }
						marks={ marks }
						max={ max }
						min={ min }
						railColor={ railColor }
						step={ step }
						value={ rangeFillValue }
					/>
					<span
						aria-hidden
						className={ clsx(
							'components-range-control__track',
							styles.track,
							{ [ styles[ 'is-disabled' ] ]: disabled },
							! disabled &&
								trackColorKeyword &&
								styles[ `background-${ trackColorKeyword }` ]
						) }
						{ ...{ disabled } }
						style={
							{
								width: fillValueOffset,
								'--_wp-components-range-control-track-color':
									trackColor || 'currentColor',
							} as React.CSSProperties
						}
					/>
					<span
						className={ clsx(
							'components-range-control__thumb-wrapper',
							styles[ 'thumb-wrapper' ],
							{ [ styles[ 'is-disabled' ] ]: disabled }
						) }
						style={ offsetStyle }
						{ ...{ disabled } }
					>
						<span
							aria-hidden
							className={ clsx( styles.thumb, {
								[ styles[ 'is-disabled' ] ]: disabled,
								[ styles[ 'is-focused' ] ]: isThumbFocused,
							} ) }
							{ ...{ disabled } }
						/>
					</span>
					{ enableTooltip && (
						<SimpleTooltip
							className="components-range-control__tooltip"
							inputRef={ inputRef }
							tooltipPlacement="bottom"
							renderTooltipContent={ renderTooltipContent }
							show={ isCurrentlyFocused || showTooltip }
							style={ offsetStyle }
							value={ value }
						/>
					) }
				</div>
				{ afterIcon && (
					<span className={ styles[ 'after-icon' ] }>
						<Icon icon={ afterIcon } />
					</span>
				) }
				{ hasInputField && (
					<NumberControl
						aria-label={ label }
						className={ clsx(
							'components-range-control__number',
							styles[ 'input-number' ]
						) }
						disabled={ disabled }
						inputMode="decimal"
						isShiftStepEnabled={ isShiftStepEnabled }
						max={ max }
						min={ min }
						onBlur={ handleOnInputNumberBlur }
						onChange={ handleOnChange }
						shiftStep={ shiftStep }
						size={ size }
						__unstableInputWidth={ `${ NUMBER_CONTROL_WIDTH }px` }
						step={ step }
						// @ts-expect-error TODO: Investigate if the `null` value is necessary
						value={ inputSliderValue }
					/>
				) }
				{ allowReset && (
					<span className={ styles[ 'action-right' ] }>
						<Button
							className="components-range-control__reset"
							// If the RangeControl itself is disabled, the reset button shouldn't be in the tab sequence.
							accessibleWhenDisabled={ ! disabled }
							// The reset button should be disabled if RangeControl itself is disabled,
							// or if the current `value` is equal to the value that would be currently
							// assigned when clicking the button.
							disabled={
								disabled ||
								value ===
									computeResetValue( {
										resetFallbackValue,
										initialPosition,
									} )
							}
							variant="secondary"
							size="small"
							onClick={ handleOnReset }
						>
							{ __( 'Reset' ) }
						</Button>
					</span>
				) }
			</div>
		</BaseControl>
	);
}

/**
 * RangeControls are used to make selections from a range of incremental values.
 *
 * ```jsx
 * import { RangeControl } from '@wordpress/components';
 * import { useState } from '@wordpress/element';
 *
 * const MyRangeControl = () => {
 *   const [ value, setValue ] = useState();
 *   return (
 *     <RangeControl
 *       help="Please select how transparent you would like this."
 *       initialPosition={ 50 }
 *       label="Opacity"
 *       max={ 100 }
 *       min={ 0 }
 *       value={ value }
 *       onChange={ setValue }
 *     />
 *   );
 * };
 * ```
 */
export const RangeControl = forwardRef( UnforwardedRangeControl );
RangeControl.displayName = 'RangeControl';

export default RangeControl;
